import React, { useState, useEffect } from 'react';
import { getConsultasAPI, getConsultaAPIDetalle } from '../../services/api';
import './ConsultasAPI.css';

const ESTADO_LABELS = {
  PENDIENTE: { label: 'Pendiente', cls: 'estado-pendiente' },
  PROCESANDO: { label: 'Procesando', cls: 'estado-procesando' },
  COMPLETADO: { label: 'Completado', cls: 'estado-completado' },
  ERROR: { label: 'Error', cls: 'estado-error' },
};

const ConsultasAPI = () => {
  const [consultas, setConsultas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ patente: '', estado: '' });

  const [selected, setSelected] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  useEffect(() => {
    fetchConsultas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters]);

  const fetchConsultas = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 20 };
      if (filters.patente) params.patente = filters.patente;
      if (filters.estado) params.estado = filters.estado;
      const data = await getConsultasAPI(params);
      setConsultas(data.items);
      setTotalPages(data.total_pages);
    } catch (err) {
      setError('Error al cargar consultas API');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handleSelect = async (consulta) => {
    setSelected(consulta);
    setLoadingDetalle(true);
    setDetalle(null);
    try {
      const data = await getConsultaAPIDetalle(consulta.id);
      setDetalle(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDetalle(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('es-AR') : '-';

  const estadoBadge = (estado) => {
    const e = ESTADO_LABELS[estado] || { label: estado, cls: '' };
    return <span className={`estado-badge ${e.cls}`}>{e.label}</span>;
  };

  return (
    <div className="capi-page">
      <h1 className="capi-title">Consultas API (Clientes)</h1>

      <div className="capi-filters">
        <div className="filter-group">
          <label>Patente</label>
          <input
            type="text"
            name="patente"
            value={filters.patente}
            onChange={handleFilterChange}
            placeholder="Buscar patente..."
          />
        </div>
        <div className="filter-group">
          <label>Estado</label>
          <select name="estado" value={filters.estado} onChange={handleFilterChange}>
            <option value="">Todos</option>
            <option value="PENDIENTE">Pendiente</option>
            <option value="PROCESANDO">Procesando</option>
            <option value="COMPLETADO">Completado</option>
            <option value="ERROR">Error</option>
          </select>
        </div>
      </div>

      <div className="capi-layout">
        {/* Lista */}
        <div className="capi-list-container">
          {loading ? (
            <div className="capi-loading">Cargando...</div>
          ) : error ? (
            <div className="capi-error">{error}</div>
          ) : (
            <>
              <div className="capi-table-container">
                <table className="capi-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Fecha</th>
                      <th>Patente</th>
                      <th>Cliente</th>
                      <th>Jurisdicciones</th>
                      <th>Resultados</th>
                      <th>Estado</th>
                      <th>Webhook</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consultas.length > 0 ? consultas.map(c => (
                      <tr
                        key={c.id}
                        className={selected?.id === c.id ? 'selected' : ''}
                        onClick={() => handleSelect(c)}
                      >
                        <td className="td-id">{c.id}</td>
                        <td className="td-date">{formatDate(c.created_at)}</td>
                        <td className="td-patente">{c.patente}</td>
                        <td className="td-cliente">{c.cliente_username || c.cliente_id}</td>
                        <td className="td-juris">
                          {c.jurisdicciones
                            ? c.jurisdicciones.join(', ')
                            : <span className="all-juris">Todas</span>}
                        </td>
                        <td className="td-resultados">{c.total_resultados}</td>
                        <td>{estadoBadge(c.estado)}</td>
                        <td>
                          <span className={`webhook-badge ${c.webhook_enviado ? 'sent' : 'pending'}`}>
                            {c.webhook_enviado ? '✓' : '—'}
                          </span>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="8" className="no-data">No hay consultas disponibles</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="pagination">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                    Anterior
                  </button>
                  <span className="page-info">Página {page} de {totalPages}</span>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                    Siguiente
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Detalle */}
        <div className="capi-detail-panel">
          {selected ? (
            <>
              <div className="detail-header">
                <h2>Detalle #{selected.id}</h2>
                <p className="detail-patente">{selected.patente}</p>
              </div>

              {loadingDetalle ? (
                <div className="capi-loading">Cargando detalle...</div>
              ) : detalle ? (
                <div className="detalle-body">
                  <div className="detalle-meta">
                    <div className="meta-row">
                      <span className="meta-label">Estado</span>
                      {estadoBadge(detalle.estado)}
                    </div>
                    <div className="meta-row">
                      <span className="meta-label">Cliente</span>
                      <span>{detalle.cliente_username || detalle.cliente_id}</span>
                    </div>
                    <div className="meta-row">
                      <span className="meta-label">Jurisdicciones</span>
                      <span>{detalle.jurisdicciones ? detalle.jurisdicciones.join(', ') : 'Todas'}</span>
                    </div>
                    <div className="meta-row">
                      <span className="meta-label">Intentos</span>
                      <span>{detalle.proceso_intentos}</span>
                    </div>
                    <div className="meta-row">
                      <span className="meta-label">Creada</span>
                      <span>{formatDate(detalle.created_at)}</span>
                    </div>
                    <div className="meta-row">
                      <span className="meta-label">Actualizada</span>
                      <span>{formatDate(detalle.updated_at)}</span>
                    </div>
                    <div className="meta-row">
                      <span className="meta-label">Webhook</span>
                      <span className={`webhook-badge ${detalle.webhook_enviado ? 'sent' : 'pending'}`}>
                        {detalle.webhook_enviado ? 'Enviado' : 'Pendiente'}
                      </span>
                    </div>
                    {detalle.webhook_error && (
                      <div className="meta-row error-row">
                        <span className="meta-label">Webhook Error</span>
                        <span className="error-text">{detalle.webhook_error}</span>
                      </div>
                    )}
                    {detalle.error_mensaje && (
                      <div className="meta-row error-row">
                        <span className="meta-label">Error</span>
                        <span className="error-text">{detalle.error_mensaje}</span>
                      </div>
                    )}
                  </div>

                  <h3 className="resultados-title">Resultados por Jurisdicción</h3>
                  {detalle.resultados && detalle.resultados.length > 0 ? (
                    <div className="resultados-list">
                      {detalle.resultados.map((r, i) => (
                        <div key={i} className={`resultado-item ${r.success ? 'ok' : 'fail'}`}>
                          <div className="resultado-juris">{r.jurisdiccion}</div>
                          <div className="resultado-stats">
                            {r.success ? (
                              <>
                                <span className="inf-count">{r.total_infracciones} infracciones</span>
                                {r.monto_total != null && (
                                  <span className="monto">${r.monto_total}</span>
                                )}
                              </>
                            ) : (
                              <span className="error-text">{r.error || 'Error'}</span>
                            )}
                          </div>
                          <span className={`status-badge ${r.success ? 'success' : 'failed'}`}>
                            {r.success ? 'OK' : 'Error'}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-data">Sin resultados aún</p>
                  )}
                </div>
              ) : null}
            </>
          ) : (
            <div className="no-selection">
              <p>Seleccione una consulta para ver el detalle</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsultasAPI;
